import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function NewsletterForm({ variant = "hero" }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState({});

  const handleSubmit = (e) => {
    e.preventDefault();
    const nextErrors = {};
    if (!name.trim() || name.trim().length < 2) {
      nextErrors.name = t(variant === "hero" ? "hero.form_error_name" : "hero.form_error_name");
    }
    if (!EMAIL_RE.test(email.trim())) {
      nextErrors.email = t("hero.form_error_email");
    }
    if (variant === "hero" && !consent) {
      nextErrors.consent = t("hero.form_error_consent");
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    navigate("/thank_you", { state: { name, email } });
  };

  const isHero = variant === "hero";
  const testidPrefix = isHero ? "hero-form" : "newsletter-form";

  return (
    <form
      id={isHero ? "lead-form" : "newsletter-form"}
      data-testid={testidPrefix}
      onSubmit={handleSubmit}
      noValidate
      className={
        isHero
          ? "bg-white border border-[#E2E8F0] rounded-2xl shadow-xl p-6 sm:p-8"
          : "bg-white border border-[#E2E8F0] rounded-2xl p-6 sm:p-8"
      }
    >
      {isHero && (
        <>
          <div className="text-xs font-mono uppercase tracking-widest text-[#2A6B7C]">
            {t("hero.badge")}
          </div>
          <h3 className="mt-2 text-xl sm:text-2xl font-semibold text-[#0F2942]">
            {t("hero.form_title")}
          </h3>
          <p className="mt-2 text-sm text-[#475569] leading-relaxed">
            {t("hero.form_subtitle")}
          </p>
        </>
      )}

      <div className={`${isHero ? "mt-6" : ""} space-y-4`}>
        <div>
          <label
            htmlFor={`${testidPrefix}-name`}
            className="block text-xs font-semibold uppercase tracking-widest text-[#0F2942]"
          >
            {t(isHero ? "hero.form_name_label" : "newsletter.name_label")}
          </label>
          <input
            id={`${testidPrefix}-name`}
            data-testid={`${testidPrefix}-name`}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t(isHero ? "hero.form_name_placeholder" : "newsletter.name_placeholder")}
            className="mt-2 w-full rounded-md border border-[#CBD5E1] bg-white px-4 py-3 text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus:border-[#0F2942] focus:outline-none focus:ring-2 focus:ring-[#0F2942]/20"
            required
          />
          {errors.name && (
            <p className="mt-1 text-xs text-[#B91C1C]" data-testid={`${testidPrefix}-name-error`}>
              {errors.name}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor={`${testidPrefix}-email`}
            className="block text-xs font-semibold uppercase tracking-widest text-[#0F2942]"
          >
            {t(isHero ? "hero.form_email_label" : "newsletter.email_label")}
          </label>
          <input
            id={`${testidPrefix}-email`}
            data-testid={`${testidPrefix}-email`}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t(isHero ? "hero.form_email_placeholder" : "newsletter.email_placeholder")}
            className="mt-2 w-full rounded-md border border-[#CBD5E1] bg-white px-4 py-3 text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus:border-[#0F2942] focus:outline-none focus:ring-2 focus:ring-[#0F2942]/20"
            required
          />
          {errors.email && (
            <p className="mt-1 text-xs text-[#B91C1C]" data-testid={`${testidPrefix}-email-error`}>
              {errors.email}
            </p>
          )}
        </div>

        {isHero && (
          <div className="flex items-start gap-3">
            <input
              id={`${testidPrefix}-consent`}
              data-testid={`${testidPrefix}-consent`}
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-[#CBD5E1] text-[#0F2942] focus:ring-[#0F2942]"
            />
            <label htmlFor={`${testidPrefix}-consent`} className="text-xs text-[#475569] leading-relaxed">
              {t("hero.form_consent")}
            </label>
          </div>
        )}
        {isHero && errors.consent && (
          <p className="text-xs text-[#B91C1C]" data-testid={`${testidPrefix}-consent-error`}>
            {errors.consent}
          </p>
        )}

        <button
          type="submit"
          data-testid={`${testidPrefix}-submit`}
          className="w-full inline-flex items-center justify-center rounded-md bg-[#0F2942] hover:bg-[#173A5E] text-white px-5 py-3 text-sm font-semibold"
        >
          {t(isHero ? "hero.form_submit" : "newsletter.submit")}
        </button>

        {isHero ? (
          <p className="text-xs text-[#64748B] leading-relaxed">{t("hero.trust_note")}</p>
        ) : (
          <p className="text-xs text-[#64748B] leading-relaxed">{t("newsletter.disclaimer")}</p>
        )}
      </div>
    </form>
  );
}
